import { z } from "zod";

export function parseDuration(v: unknown): number {
  if (typeof v === "number") return v * 1000;
  if (typeof v !== "string") throw new Error("Thời gian phải là số giây hoặc chuỗi như '30s', '5m'");
  const m = /^\s*(\d+(?:\.\d+)?)\s*(ms|s|m|h)?\s*$/.exec(v);
  if (!m) throw new Error(`Thời gian không hợp lệ: '${v}' (ví dụ: 500ms, 30s, 5m, 1h)`);
  const n = parseFloat(m[1]!);
  const unit = m[2] ?? "s";
  return unit === "ms" ? n : unit === "s" ? n * 1000 : unit === "m" ? n * 60_000 : n * 3_600_000;
}

const Duration = z.union([z.number().nonnegative(), z.string()]).superRefine((v, ctx) => {
  try { parseDuration(v); } catch (e) { ctx.addIssue({ code: "custom", message: (e as Error).message }); }
});

const Ident = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/, "Định danh chỉ gồm chữ không dấu, số, gạch dưới; bắt đầu bằng chữ");
const Slug = z.string().min(1).max(80).regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, "Tên chỉ gồm chữ không dấu, số, '-' và '_'");

export const TargetSchema = z.strictObject({
  kind: z.enum(["web", "desktop", "android"]),
  mode: z.enum(["attach", "launch"]).default("attach"),
  url: z.string().url("url phải là địa chỉ đầy đủ, ví dụ https://erp.noi-bo.vn").optional(),
  app: z.string().optional(),
  profiles: z.strictObject({ dir: z.string(), port_base: z.number().int().optional() }).optional(),
}).superRefine((t, ctx) => {
  if (t.kind !== "web") ctx.addIssue({ code: "custom", message: `Nền tảng '${t.kind}' chưa hỗ trợ trong extension — v1 chỉ chạy web`, path: ["kind"] });
  if (t.mode === "launch") ctx.addIssue({ code: "custom", message: "Extension luôn chạy trong Chrome đang mở (attach), không launch", path: ["mode"] });
});

export const ParamSchema = z.strictObject({
  type: z.enum(["string", "number", "path", "date", "bool"]).default("string"),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
  required: z.boolean().default(false),
  label: z.string().optional(),
});

export const SourceSchema = z.strictObject({
  kind: z.enum(["excel", "csv", "json", "sqlite", "api"]),
  path: z.string().optional(),
  url: z.string().optional(),
  sheet: z.string().optional(),
  header_row: z.number().int().positive().optional(),
  data: z.unknown().optional(),
}).superRefine((s, ctx) => {
  if (s.kind === "excel" || s.kind === "sqlite") ctx.addIssue({ code: "custom", message: `Nguồn '${s.kind}' chưa hỗ trợ trong extension; dùng csv hoặc json (qua url hoặc data)`, path: ["kind"] });
  if (!s.url && s.data === undefined && !s.path) ctx.addIssue({ code: "custom", message: "Nguồn cần url, path hoặc data" });
});

export const KnowledgeSchema = z.object({
  giong_dieu: z.string().optional(),
  cam_ky: z.array(z.string()).optional(),
  tu_khoa: z.record(z.string(), z.array(z.string())).optional(),
  mau_tra_loi: z.record(z.string(), z.string()).optional(),
}).catchall(z.unknown());

export const InterruptSchema = z.strictObject({
  name: Ident,
  when_see: z.string().min(1),
  do: z.string().optional(),
  then: z.enum(["continue", "retry_step", "halt"]),
});

export const RetrySchema = z.union([
  z.number().int().min(0).max(10),
  z.strictObject({
    times: z.number().int().min(0).max(10),
    backoff: z.array(Duration).optional(),
    only_if: z.union([z.string(), z.array(z.enum(["network", "timeout", "not_found", "any"]))]).optional(),
  }),
]);

export const ExpectApiSchema = z.union([
  z.string().url(),
  z.strictObject({
    url: z.string(),
    method: z.enum(["GET", "POST"]).default("GET"),
    body: z.unknown().optional(),
    expect_status: z.number().int().optional(),
    json_path: z.string().optional(),
    equals: z.unknown().optional(),
    contains: z.string().optional(),
  }),
]);

export const ClassifySchema = z.strictObject({
  by: z.enum(["keyword", "model"]).default("keyword"),
  fallback: z.enum(["model", "none"]).default("none"),
  buckets: z.array(Ident).min(2),
  input: z.string().optional(),
  default_bucket: Ident.optional(),
  anh: z.string().optional(), // biểu thức URL ảnh của mục (vd item.anh_url) — máy chỉ gửi ảnh cho AI khi chữ ngắn (AI nhìn khi cần)
});

export const GenerateSchema = z.strictObject({
  input: z.string(),
  guide: z.union([z.literal("knowledge"), z.string()]).optional(),
  max_words: z.number().int().positive().max(2000).default(60),
  anh: z.string().optional(), // như classify.anh: ảnh của bài/bình luận để AI hiểu khi chữ không đủ ý
  max_chars: z.number().int().positive().max(10_000).optional(), // giới hạn ký tự của nền tảng (X: 280) — lớp kiểm cứng, soạn lại nếu vượt
});

export const PaginateSchema = z.strictObject({
  next: z.string().min(1),
  until: z.string().min(1),
  max_pages: z.number().int().positive().max(1000),
});

// Các trường "chính" — mỗi bước phải có đúng một.
export const PRIMARY_KEYS = ["do", "extract", "classify", "template", "generate", "filter", "paginate", "wait", "wait_for", "wait_file", "steps"] as const;
export type PrimaryKey = (typeof PRIMARY_KEYS)[number];

export interface StepInput {
  id?: string;
  on?: string;
  do?: string;
  expect?: string;
  expect_file?: string;
  expect_api?: z.input<typeof ExpectApiSchema>;
  risk?: "low" | "high";
  timeout?: number | string;
  retry?: z.input<typeof RetrySchema>;
  optional?: boolean;
  idempotent?: "safe" | "guarded" | "unsafe";
  auto_approve?: boolean;
  ledger?: string;
  when?: string;
  // Lý do dễ hiểu khi `when` không thoả = "không có việc để làm" (không phải lỗi) — xem BundleStep.ly_do_bo_qua. Chỉ dùng kèm `when`.
  ly_do_bo_qua?: string;
  for_each?: string;
  max_items?: number;
  on_item_error?: "skip" | "halt";
  for_each_profile?: boolean;
  steps?: StepInput[];
  extract?: string;
  // Bước extract danh sách: dưới ngưỡng này coi là LỖI bước (trang chưa vẽ xong, lưới báo lỗi tạm…) → được chẩn đoán/tải lại như lỗi khác,
  // thay vì "0 mục" rồi các bước sau lặng lẽ không có gì để làm.
  min_items?: number;
  into?: string;
  filter?: string;
  classify?: z.input<typeof ClassifySchema>;
  template?: string;
  generate?: z.input<typeof GenerateSchema>;
  paginate?: z.input<typeof PaginateSchema>;
  wait?: number | string;
  // Nghỉ NGẪU NHIÊN trong [wait, wait_max] (bước "nghỉ" như người của Kịch bản sơ đồ). Chỉ dùng kèm `wait`.
  wait_max?: number | string;
  wait_for?: string;
  wait_file?: string;
  on_timeout?: "halt" | "continue";
}

export const StepSchema: z.ZodType<StepInput> = z.lazy(() =>
  z.strictObject({
    id: Ident.optional(),
    on: Ident.optional(),
    do: z.string().min(1).optional(),
    expect: z.string().min(1).optional(),
    expect_file: z.string().min(1).optional(),
    expect_api: ExpectApiSchema.optional(),
    risk: z.enum(["low", "high"]).optional(),
    timeout: Duration.optional(),
    retry: RetrySchema.optional(),
    optional: z.boolean().optional(),
    idempotent: z.enum(["safe", "guarded", "unsafe"]).optional(),
    // (03/10/2026) Bước rủi ro cao chạy thật KHÔNG hỏi duyệt (công tắc "Tự động" của Kịch bản sơ đồ) — xem BundleStep.auto_approve.
    auto_approve: z.boolean().optional(),
    ledger: Ident.optional(),
    when: z.string().min(1).optional(),
    ly_do_bo_qua: z.string().trim().min(1, "ly_do_bo_qua không được rỗng").max(300, "ly_do_bo_qua tối đa 300 ký tự").optional(),
    for_each: z.string().min(1).optional(),
    max_items: z.number().int().positive().optional(),
    on_item_error: z.enum(["skip", "halt"]).optional(),
    for_each_profile: z.boolean().optional(),
    steps: z.array(StepSchema).min(1).optional(),
    extract: z.string().min(1).optional(),
    min_items: z.number().int().positive().optional(),
    into: z.string().regex(/^[A-Za-z_][A-Za-z0-9_.]*$/, "into phải là tên biến, ví dụ dsbl hoặc item.loai").optional(),
    filter: z.string().min(1).optional(),
    classify: ClassifySchema.optional(),
    template: z.string().min(1).optional(),
    generate: GenerateSchema.optional(),
    paginate: PaginateSchema.optional(),
    wait: Duration.optional(),
    wait_max: Duration.optional(),
    wait_for: z.string().min(1).optional(),
    wait_file: z.string().min(1).optional(),
    on_timeout: z.enum(["halt", "continue"]).optional(),
  }).superRefine((s, ctx) => {
    const present = PRIMARY_KEYS.filter((k) => s[k] !== undefined);
    const isPureLoop = present.length === 0 && s.for_each !== undefined;
    if (present.length === 0 && !isPureLoop) {
      ctx.addIssue({ code: "custom", message: `Bước cần một trong: ${PRIMARY_KEYS.join(", ")}` });
    }
    if (present.length > 1 && !(present.length === 2 && present.includes("paginate") && present.includes("extract"))) {
      ctx.addIssue({ code: "custom", message: `Bước chỉ được có một hành động chính, đang có: ${present.join(", ")}` });
    }
    for (const k of ["extract", "classify", "template", "generate"] as const) {
      if (s[k] !== undefined && !s.into) ctx.addIssue({ code: "custom", message: `Bước '${k}' cần 'into' để đặt tên biến kết quả`, path: [k] });
    }
    if (s.ly_do_bo_qua !== undefined && s.when === undefined) ctx.addIssue({ code: "custom", message: "ly_do_bo_qua chỉ dùng kèm when (lý do khi điều kiện của bước không thoả)", path: ["ly_do_bo_qua"] });
    if (s.min_items !== undefined && s.extract === undefined) ctx.addIssue({ code: "custom", message: "min_items chỉ dùng cho bước 'extract' (danh sách)", path: ["min_items"] });
    if (s.do !== undefined && !s.on) ctx.addIssue({ code: "custom", message: "Bước 'do' cần 'on' để biết chạy trên target nào", path: ["on"] });
    if (s.paginate !== undefined && !s.on) ctx.addIssue({ code: "custom", message: "Bước 'paginate' cần 'on'", path: ["on"] });
    if (s.wait_max !== undefined) {
      if (s.wait === undefined) ctx.addIssue({ code: "custom", message: "wait_max chỉ dùng kèm wait (nghỉ ngẫu nhiên từ wait tới wait_max)", path: ["wait_max"] });
      else { try { if (parseDuration(s.wait_max) < parseDuration(s.wait)) ctx.addIssue({ code: "custom", message: "wait_max phải lớn hơn hoặc bằng wait", path: ["wait_max"] }); } catch { /* Duration đã báo lỗi */ } }
    }
    if (s.auto_approve !== undefined && s.risk !== "high") ctx.addIssue({ code: "custom", message: "auto_approve chỉ dùng cho bước risk: high", path: ["auto_approve"] });
    if (s.idempotent === "unsafe" && s.risk !== "high") {
      ctx.addIssue({ code: "custom", message: "Bước idempotent: unsafe phải có risk: high", path: ["risk"] });
    }
  })
);

export const ScriptSchema = z.strictObject({
  name: Slug,
  version: z.number().int().positive().default(1),
  description: z.string().optional(),
  targets: z.record(Ident, TargetSchema).default({}),
  params: z.record(Ident, ParamSchema).default({}),
  secrets: z.array(Ident).default([]),
  sources: z.record(Ident, SourceSchema).default({}),
  knowledge: KnowledgeSchema.default({}),
  interrupts: z.array(InterruptSchema).default([]),
  steps: z.array(StepSchema).min(1, "Kịch bản cần ít nhất một bước"),
});

export type ScriptInput = z.input<typeof ScriptSchema>;
export type Script = z.output<typeof ScriptSchema>;
export type Step = StepInput;
export type Target = z.output<typeof TargetSchema>;
export type Param = z.output<typeof ParamSchema>;
export type Knowledge = z.output<typeof KnowledgeSchema>;
export type Interrupt = z.output<typeof InterruptSchema>;
export type Classify = z.output<typeof ClassifySchema>;
export type Generate = z.output<typeof GenerateSchema>;
export type Paginate = z.output<typeof PaginateSchema>;
export type ExpectApi = z.output<typeof ExpectApiSchema>;
